/**
 * Storefront market detection — data comes from GET /api/markets (see market_config.py).
 * No duplicate market list: call initMarketsFromPayload() after fetching the API.
 */

/** @type {Record<string, { country_names?: string[], locale_codes?: string[], currency_code?: string, currency_symbol?: string }> | null} */
let marketsByCode = null;

/** @type {string} */
let defaultMarketCode = 'US';

/**
 * @param {{ markets?: Record<string, object>, default_market?: string } | null} payload
 */
export function initMarketsFromPayload(payload) {
  if (!payload || !payload.markets || typeof payload.markets !== 'object') {
    marketsByCode = null;
    defaultMarketCode = 'US';
    return;
  }
  marketsByCode = payload.markets;
  defaultMarketCode =
    typeof payload.default_market === 'string' && payload.default_market
      ? payload.default_market.toUpperCase()
      : 'US';
}

export function hasMarketsLoaded() {
  return marketsByCode != null && Object.keys(marketsByCode).length > 0;
}

/**
 * @returns {string[]}
 */
export function getAllMarketCodes() {
  if (!marketsByCode) return [defaultMarketCode];
  return Object.keys(marketsByCode);
}

/**
 * @param {string} code
 */
export function getMarketConfig(code) {
  if (!marketsByCode || !code) return null;
  return marketsByCode[code.toUpperCase()] || null;
}

/**
 * @param {string} countryName
 * @returns {string|null}
 */
export function parseMarketFromCountry(countryName) {
  if (!countryName || !marketsByCode) return null;

  const upperName = countryName.toUpperCase().trim();

  if (marketsByCode[upperName]) {
    return upperName;
  }

  for (const [code, config] of Object.entries(marketsByCode)) {
    const names = config.country_names || [];
    if (names.map((n) => String(n).toUpperCase()).includes(upperName)) {
      return code;
    }
  }

  return null;
}

/**
 * @param {string} currencyCode
 * @returns {string|null}
 */
export function getMarketFromCurrency(currencyCode) {
  if (!currencyCode || !marketsByCode) return null;

  const upperCurrency = currencyCode.toUpperCase().trim();

  for (const [code, config] of Object.entries(marketsByCode)) {
    if ((config.currency_code || '').toUpperCase() === upperCurrency) {
      return code;
    }
  }

  return null;
}

/**
 * @param {string} locale
 * @returns {string|null}
 */
export function getMarketFromLocale(locale) {
  if (!locale || !marketsByCode) return null;

  const lowerLocale = locale.toLowerCase().trim();

  for (const [code, config] of Object.entries(marketsByCode)) {
    const locales = config.locale_codes || [];
    for (const localeCode of locales) {
      if (lowerLocale.startsWith(String(localeCode).toLowerCase())) {
        return code;
      }
    }
  }

  return null;
}

/**
 * Detect storefront market for API `market` (uses loaded /api/markets data).
 * @returns {string}
 */
export function detectUserMarket() {
  if (!marketsByCode || Object.keys(marketsByCode).length === 0) {
    return defaultMarketCode;
  }

  if (typeof window !== 'undefined' && window.Shopify?.country) {
    const code = parseMarketFromCountry(window.Shopify.country);
    if (code) {
      console.log(`[Market detection] Shopify.country → ${code}`);
      return code;
    }
  }

  if (typeof window !== 'undefined' && window.Shopify?.currency?.active) {
    const code = getMarketFromCurrency(window.Shopify.currency.active);
    if (code) {
      console.log(`[Market detection] Shopify.currency → ${code}`);
      return code;
    }
  }

  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const countryParam = urlParams.get('country');
    if (countryParam) {
      const code = parseMarketFromCountry(countryParam);
      if (code) {
        console.log(`[Market detection] URL country param → ${code}`);
        return code;
      }
    }
  }

  if (typeof navigator !== 'undefined') {
    const locale = navigator.language || navigator.userLanguage;
    if (locale) {
      const code = getMarketFromLocale(locale);
      if (code) {
        console.log(`[Market detection] Browser locale → ${code}`);
        return code;
      }
    }
  }

  console.log(`[Market detection] Default → ${defaultMarketCode}`);
  return defaultMarketCode;
}

/**
 * @param {string} marketCode
 * @returns {string}
 */
export function getCurrencySymbol(marketCode) {
  const config = getMarketConfig(marketCode);
  return config?.currency_symbol || '$';
}

/**
 * @param {string} marketCode
 * @returns {string}
 */
export function getCurrencyCode(marketCode) {
  const config = getMarketConfig(marketCode);
  return config?.currency_code || 'USD';
}

/**
 * @param {string} marketCode
 * @returns {boolean}
 */
export function isValidMarket(marketCode) {
  if (!marketsByCode) return marketCode?.toUpperCase() === defaultMarketCode;
  return Object.prototype.hasOwnProperty.call(
    marketsByCode,
    marketCode?.toUpperCase()
  );
}
